import { defineStore } from 'pinia'
import { ref, reactive, computed, watch, nextTick } from 'vue'
import { listCategories, analyzeAttributes, buildPack } from '@/api/exportApi'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const PRODUCT_COUNT = 3
const PRODUCT_LABELS = ['商品 A', '商品 B', '商品 C']

// 尺码大小排序（从小到大），用于找出用户选的最大尺码
const SIZE_ORDER = [
  '6X-Small(6-小)', '5X-Small(5-小)', '4X-Small(4-小)', '3X-Small(3-小)',
  'XX-Small(XX-小)', 'X-Small(X-小)',
  'Small(小)', 'Medium(中等)', 'Large(大)',
  'X-Large(X-大号)', 'XX-Large(XX-大号)',
  '3X-Large(3-大)', '4X-Large(4-大)', '5X-Large(5-大)', '6X-Large(6-大)',
  '7X-Large(7-大)', '8X-Large(8-大号)', '9X-Large(9x大)', '10X-Large(10-大)',
  '0X', '1X', '2X', '3X', '4X', '5X', '6X', '7X', '8X', '9X', '10X'
]

// 颜色选项（dianxiaomi Color Map 的 20 色标准），value 是英文，label 用于 UI 显示
const COLOR_OPTIONS = [
  { value: 'Beige', label: '米色（Beige）' },
  { value: 'Black', label: '黑色（Black）' },
  { value: 'Blue', label: '蓝色（Blue）' },
  { value: 'Bronze', label: '古铜色（Bronze）' },
  { value: 'Brown', label: '棕色（Brown）' },
  { value: 'Clear', label: '透明（Clear）' },
  { value: 'Gold', label: '金色（Gold）' },
  { value: 'Green', label: '绿色（Green）' },
  { value: 'Grey', label: '灰色（Grey）' },
  { value: 'Metallic', label: '金属色（Metallic）' },
  { value: 'Multicolor', label: '多色（Multicolor）' },
  { value: 'Off White', label: '米白色（Off White）' },
  { value: 'Orange', label: '橙色（Orange）' },
  { value: 'Pink', label: '粉色（Pink）' },
  { value: 'Purple', label: '紫色（Purple）' },
  { value: 'Red', label: '红色（Red）' },
  { value: 'Silver', label: '银色（Silver）' },
  { value: 'Turquoise', label: '青绿色（Turquoise）' },
  { value: 'White', label: '白色（White）' },
  { value: 'Yellow', label: '黄色（Yellow）' }
]

const createDefaultForm = () => ({
  sku: '',
  storeAccount: '',
  categoryKey: '',
  price: '39.9',
  quantity: '1000',
  sizeList: 'Small(小),Medium(中等),Large(大)',
  brand: ''
})

const STORAGE_KEY = 'zhipan.export.productCache'
const INDEX_KEY = 'zhipan.export.currentProductIndex'

const loadPersistedCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length !== PRODUCT_COUNT) return null
    return parsed
  } catch (e) { return null }
}

const loadPersistedIndex = () => {
  try {
    const v = parseInt(localStorage.getItem(INDEX_KEY), 10)
    if (!Number.isNaN(v) && v >= 0 && v < PRODUCT_COUNT) return v
  } catch (e) { /* ignore */ }
  return 0
}

export const useExportStore = defineStore('export', () => {
  const workbench = useWorkbenchStore()

  const categories = ref([])
  const categoriesLoading = ref(false)

  // 从 localStorage 恢复
  const persisted = loadPersistedCache()

  // 当前选中产品索引（0=A, 1=B, 2=C）
  const currentProductIndex = ref(loadPersistedIndex())

  // 每个产品的状态缓存
  const productCache = ref(
    persisted
      ? persisted.map(p => ({
        form: { ...createDefaultForm(), ...(p.form || {}) },
        analyzedFields: { ...(p.analyzedFields || {}) },
        variantColors: { ...(p.variantColors || {}) }
      }))
      : Array.from({ length: PRODUCT_COUNT }, () => ({
        form: createDefaultForm(),
        analyzedFields: {},
        variantColors: {}
      }))
  )

  // 当前产品的镜像（UI 绑定用）
  const initialIdx = currentProductIndex.value
  const form = ref({ ...productCache.value[initialIdx].form })
  const analyzedFields = ref({ ...productCache.value[initialIdx].analyzedFields })
  const variantColors = ref({ ...productCache.value[initialIdx].variantColors })

  // 图片映射的人工调整只保留在当前会话中，避免把大体积 base64 图片写入 localStorage。
  // key 存在且值为空字符串表示该位置被用户删除；非空值表示重新上传的图片。
  const imageOverrides = ref(Array.from({ length: PRODUCT_COUNT }, () => ({})))

  const analyzing = ref(false)
  const analyzeError = ref('')
  const building = ref(false)
  const buildError = ref('')

  // ─── 后台打包任务队列 ───
  // 点击下载后不再阻塞 UI：任务进队列后台串行执行，UI 通过 packJobs 展示进度，
  // 完成后自动触发浏览器下载，并保留“重新下载”入口。
  const packJobs = ref([])
  let _jobSeq = 0
  let _queueTail = Promise.resolve()

  const triggerBrowserDownload = (url, filename) => {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  // 后台执行单个打包任务（不抛错，错误写入 job）
  const _executeJob = async (job, pi) => {
    job.status = 'packing'
    try {
      const cached = productCache.value[pi]
      const productJson = buildProductJsonForProduct(pi)
      const images = buildImageListForProduct(pi)
      const blob = await buildPack(cached.form.sku, productJson, images)
      const url = URL.createObjectURL(blob)
      const filename = `pack_${cached.form.sku}.zip`
      job.resultUrl = url
      job.filename = filename
      job.status = 'done'
      // 完成后自动下载一次
      triggerBrowserDownload(url, filename)
    } catch (e) {
      job.error = e.message || '打包失败'
      job.status = 'error'
    }
  }

  // 重新下载某个已完成的任务
  const redownloadPack = (jobId) => {
    const job = packJobs.value.find(j => j.id === jobId)
    if (job?.status === 'done' && job.resultUrl) {
      triggerBrowserDownload(job.resultUrl, job.filename)
    }
  }

  // 清除已完成/失败的任务（保留打包中的），并回收其 blob URL
  const clearFinishedJobs = () => {
    packJobs.value
      .filter(j => j.status === 'done' || j.status === 'error')
      .forEach(j => { if (j.resultUrl) URL.revokeObjectURL(j.resultUrl) })
    packJobs.value = packJobs.value.filter(j => j.status === 'packing' || j.status === 'queued')
  }

  const hasActiveJob = computed(() => packJobs.value.some(j => j.status === 'packing' || j.status === 'queued'))

  // 切换产品（保存当前 → 加载目标）
  let switching = false
  const switchProduct = (idx) => {
    if (idx === currentProductIndex.value || idx < 0 || idx >= PRODUCT_COUNT) return
    productCache.value[currentProductIndex.value] = {
      form: { ...form.value },
      analyzedFields: { ...analyzedFields.value },
      variantColors: { ...variantColors.value }
    }
    switching = true
    currentProductIndex.value = idx
    const cached = productCache.value[idx]
    form.value = { ...cached.form }
    analyzedFields.value = { ...cached.analyzedFields }
    variantColors.value = { ...cached.variantColors }
    nextTick(() => { switching = false })
  }

  // 把当前镜像同步回缓存（下载前调用）
  const syncCurrentToCache = () => {
    productCache.value[currentProductIndex.value] = {
      form: { ...form.value },
      analyzedFields: { ...analyzedFields.value },
      variantColors: { ...variantColors.value }
    }
  }

  // === 各类按 pi 取数据的函数 ===
  const getSellType = (pi) => {
    const colors = workbench.imageGen?.fissionColors?.[pi] || []
    return colors.length > 0 ? '多变种' : '单品'
  }
  const getIsMultiVariant = (pi) => getSellType(pi) === '多变种'

  const getColorCount = (pi) => {
    if (!getIsMultiVariant(pi)) return 0
    const colors = workbench.imageGen?.fissionColors?.[pi] || []
    const fissionCount = colors.filter(c => (c.results || []).some(r => r.url)).length
    const promoSlots = workbench.imageGen?.genResults?.[pi] || []
    const hasPromos = promoSlots.some(s => s.url)
    return (hasPromos ? 1 : 0) + fissionCount
  }

  const getProductImages = (pi) => {
    const out = []
    const mainImg = workbench.products?.[pi]?.mainImage
    if (mainImg) out.push({ label: '产品原图', url: mainImg })
    const promoSlots = workbench.imageGen?.genResults?.[pi] || []
    promoSlots.forEach((slot, si) => {
      if (slot.url) out.push({ label: `宣传图${si + 1}`, url: slot.url })
    })
    const colors = workbench.imageGen?.fissionColors?.[pi] || []
    colors.forEach((color, ci) => {
      const fissionResults = color.results || []
      fissionResults.forEach((r, ri) => {
        if (r.url) out.push({
          label: `${color.label || '颜色' + (ci + 1)}_${ri + 1}`,
          url: r.url, ci, ri
        })
      })
    })
    return out
  }

  const getImageMapping = (pi) => {
    const map = { single: [], variants: [] }
    const overrides = imageOverrides.value[pi] || {}
    const resolveImage = (key, sourceUrl = '') =>
      Object.prototype.hasOwnProperty.call(overrides, key) ? overrides[key] : sourceUrl
    if (getIsMultiVariant(pi)) {
      const colors = workbench.imageGen?.fissionColors?.[pi] || []
      const mainResults = workbench.mainImageGen?.results || {}
      const promoSlots = workbench.imageGen?.genResults?.[pi] || []
      let vi = 0

      const firstPromoIndex = promoSlots.findIndex(slot => slot.url)
      if (firstPromoIndex >= 0) {
        const firstPromoUrl = promoSlots[firstPromoIndex].url
        const whiteKey = `promo-${pi}-${firstPromoIndex}`
        const white = mainResults[whiteKey]?.url
        const mappingKey = 'promo'
        const subUrls = Array.from({ length: 6 }, (_, ri) =>
          resolveImage(`${mappingKey}:sub:${ri}`, promoSlots[ri]?.url || '')
        )
        map.variants.push({
          vi: vi++,
          type: 'promo',
          mappingKey,
          colorLabel: '颜色 1',
          colorValue: 'color_1',
          mainKey: `${mappingKey}:main`,
          swatchKey: `${mappingKey}:swatch`,
          subKeys: Array.from({ length: 6 }, (_, ri) => `${mappingKey}:sub:${ri}`),
          main: resolveImage(`${mappingKey}:main`, white || firstPromoUrl),
          swatch: resolveImage(`${mappingKey}:swatch`, white || firstPromoUrl),
          sub: subUrls
        })
      }

      colors.forEach((color, ci) => {
        const fissionResults = color.results || []
        if (!fissionResults.some(r => r.url)) return
        const mainKey = `${pi}-${ci}`
        const whiteMain = mainResults[mainKey]?.url
        const firstResult = fissionResults.find(r => r.url)?.url || ''
        const mappingKey = `fission-${ci}`
        const subUrls = Array.from({ length: 6 }, (_, ri) =>
          resolveImage(`${mappingKey}:sub:${ri}`, fissionResults[ri]?.url || '')
        )
        map.variants.push({
          vi: vi++,
          type: 'fission',
          ci,
          mappingKey,
          colorLabel: `颜色 ${ci + 2}`,
          colorValue: `color_${ci + 2}`,
          mainKey: `${mappingKey}:main`,
          swatchKey: `${mappingKey}:swatch`,
          subKeys: Array.from({ length: 6 }, (_, ri) => `${mappingKey}:sub:${ri}`),
          main: resolveImage(`${mappingKey}:main`, whiteMain || firstResult),
          swatch: resolveImage(`${mappingKey}:swatch`, whiteMain || firstResult),
          sub: subUrls
        })
      })
    } else {
      const promoSlots = workbench.imageGen?.genResults?.[pi] || []
      const mainResults = workbench.mainImageGen?.results || {}
      promoSlots.forEach((slot, si) => {
        if (slot.url) {
          const whiteKey = `promo-${pi}-${si}`
          const white = mainResults[whiteKey]?.url
          const key = `single:${si}`
          map.single.push({ url: resolveImage(key, white || slot.url), label: `Promo${si + 1}`, key })
        }
      })
    }
    return map
  }

  const getCopyData = (pi) => {
    const r = workbench.copywriting?.results?.[pi]
    if (!r) return null
    return {
      title: r.title || r.titleZh || '',
      introduction: r.introduction || r.introductionZh || '',
      features: r.features?.length ? r.features : (r.featuresZh || []),
      keywords: r.keywords || ''
    }
  }

  // === 当前产品的 computeds（UI 用）===
  const sellType = computed(() => getSellType(currentProductIndex.value))
  const isMultiVariant = computed(() => getIsMultiVariant(currentProductIndex.value))
  const colorCount = computed(() => getColorCount(currentProductIndex.value))
  const productImages = computed(() => getProductImages(currentProductIndex.value))
  const imageMapping = computed(() => getImageMapping(currentProductIndex.value))
  const copyData = computed(() => getCopyData(currentProductIndex.value))

  // 尺码字段诊断：暴露判定依据，便于排查“为何有/无尺码字段”
  const sizeDiag = computed(() => {
    const pi = currentProductIndex.value
    const colors = workbench.imageGen?.fissionColors?.[pi] || []
    const colorDetails = colors.map((c, ci) => ({
      idx: ci,
      label: c.label || `颜色${ci + 1}`,
      hasRef: !!(c.refImage || c.refPreview),
      resultCount: (c.results || []).filter(r => r.url).length
    }))
    const fields = currentTemplateFields.value
    const sizeFields = fields
      .filter(f => f.key === 'Size' || f.key.endsWith('Size Value'))
      .map(f => ({ key: f.key, name: f.displayName }))
    return {
      pi,
      categoryKey: form.value.categoryKey || '(未选)',
      fissionColorCount: colors.length,          // >0 即判为多变种 → 显示「尺码列表」选择器
      sellType: sellType.value,
      isMultiVariant: isMultiVariant.value,      // 「尺码列表」选择器 v-if 只看它
      colorCount: colorCount.value,
      sizeFields,                                 // 类别模板里识别到的尺码字段（仅用于 *Size Value 自动填充）
      colors: colorDetails
    }
  })

  const uploadMappingImage = (slotKey, file) => new Promise((resolve, reject) => {
    if (!slotKey || !file) {
      reject(new Error('请选择要上传的图片'))
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      imageOverrides.value[currentProductIndex.value][slotKey] = String(reader.result || '')
      resolve()
    }
    reader.onerror = () => reject(new Error('图片读取失败'))
    reader.readAsDataURL(file)
  })

  const loadCategories = async () => {
    if (categories.value.length > 0) return
    categoriesLoading.value = true
    try {
      categories.value = await listCategories()
      productCache.value.forEach(p => {
        if (!p.form.categoryKey && categories.value.length > 0) {
          p.form.categoryKey = categories.value[0].key
        }
      })
      if (!form.value.categoryKey && categories.value.length > 0) {
        form.value.categoryKey = categories.value[0].key
      }
    } catch (e) {
      console.error('加载类别失败', e)
    } finally {
      categoriesLoading.value = false
    }
  }

  const currentCategory = computed(() =>
    categories.value.find(c => c.key === form.value.categoryKey) || null
  )
  const currentTemplateFields = computed(() => currentCategory.value?.fields || [])

  const aiDerivableFields = computed(() =>
    currentTemplateFields.value.filter(f => {
      if ((f.source || 'excel').toLowerCase() !== 'excel') return false
      const dv = f.defaultValue
      if (dv && dv !== 'false') return false
      if (f.key === 'List Price') return false
      if (f.key === 'Color Map') return false
      if (f.key.endsWith('Size Value')) return false
      return true
    })
  )

  const sizeValues = computed(() =>
    form.value.sizeList.split(/[,，]/).map(s => s.trim()).filter(Boolean)
  )

  const fillDefaults = () => {
    const fields = currentTemplateFields.value
    const next = {}
    fields.forEach(f => {
      const dv = f.defaultValue
      if (!dv || dv === 'false') return
      if ((f.source || 'excel').toLowerCase() !== 'excel') return
      if (f.type === 'multi_checkbox') {
        next[f.key] = [dv]
      } else {
        next[f.key] = dv
      }
    })
    analyzedFields.value = next
  }

  // 类别变化时预填默认值（切换产品时跳过，避免覆盖缓存）
  watch(() => form.value.categoryKey, (key, oldKey) => {
    if (switching) return
    if (key && key !== oldKey) fillDefaults()
  })

  // List Price = 不含税价目表，同步自表单价格
  watch(() => form.value.price, (price) => {
    if (price) analyzedFields.value['List Price'] = price
  }, { immediate: true })

  // *Size Value = 尺码数值，取用户选的最大尺码
  const sizeValueFieldKey = computed(() => {
    const f = currentTemplateFields.value.find(it => it.key.endsWith('Size Value'))
    return f?.key || ''
  })
  const largestSize = computed(() => {
    const selected = sizeValues.value
    if (selected.length === 0) return ''
    let maxIdx = -1
    let maxSize = ''
    selected.forEach(s => {
      const idx = SIZE_ORDER.indexOf(s)
      if (idx > maxIdx) { maxIdx = idx; maxSize = s }
    })
    return maxSize
  })
  watch([largestSize, sizeValueFieldKey], ([size, key]) => {
    if (!key) return
    if (size) {
      analyzedFields.value[key] = size
    } else {
      delete analyzedFields.value[key]
    }
  }, { immediate: true })

  // 持久化到 localStorage：当前镜像变化 → 同步到 productCache → 写盘
  watch(
    [form, analyzedFields, variantColors, currentProductIndex],
    () => {
      productCache.value[currentProductIndex.value] = {
        form: { ...form.value },
        analyzedFields: { ...analyzedFields.value },
        variantColors: { ...variantColors.value }
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(productCache.value))
        localStorage.setItem(INDEX_KEY, String(currentProductIndex.value))
      } catch (e) {
        console.warn('[export] 持久化失败', e)
      }
    },
    { deep: true }
  )

  // 触发 AI 分析（当前产品）
  const runAnalyze = async () => {
    if (!form.value.categoryKey) {
      analyzeError.value = '请先选择商品类别'
      return
    }
    const imgs = productImages.value.map(i => i.url)
    if (imgs.length === 0) {
      analyzeError.value = `${PRODUCT_LABELS[currentProductIndex.value]} 暂无图片，请先到图片生成页生成`
      return
    }
    analyzing.value = true
    analyzeError.value = ''
    try {
      const title = copyData.value?.title || ''
      const fabric = workbench.copywriting?.inputs?.[currentProductIndex.value]?.fabric?.trim() || ''
      const res = await analyzeAttributes(form.value.categoryKey, imgs, title, fabric)
      const merged = { ...analyzedFields.value, ...(res.fields || {}) }
      const cm = merged['Color Map']
      if (cm) {
        const next = { ...variantColors.value }
        if (Array.isArray(cm)) {
          cm.forEach((c, idx) => {
            if (c) next[`color_${idx + 1}`] = c
          })
          if (cm.length > 1) {
            merged['Color Map'] = 'Multicolor'
          } else if (cm.length === 1 && cm[0]) {
            merged['Color Map'] = cm[0]
          } else {
            delete merged['Color Map']
          }
        } else if (cm) {
          next.color_1 = cm
        }
        variantColors.value = next
      }
      analyzedFields.value = merged
    } catch (e) {
      analyzeError.value = e.message || '分析失败'
    } finally {
      analyzing.value = false
    }
  }

  // === 构造 product.json（任意产品）===
  const buildMatrix = (variants, sizes, vc) => {
    const matrix = []
    variants.forEach(v => {
      const subs = v.sub
        .map((url, ri) => url ? `images/sub_${v.vi + 1}_${ri + 1}.png` : '')
        .filter(Boolean)
      const colorName = vc[v.colorValue] || v.colorValue
      const imageFields = {
        ...(v.main ? { main: `images/main_${v.vi + 1}.png` } : {}),
        ...(v.swatch ? { swatch: `images/swatch_${v.vi + 1}.png` } : {}),
        ...(subs.length > 0 ? { sub: subs } : {})
      }
      if (sizes.length === 0) {
        matrix.push({
          color: colorName,
          ...imageFields
        })
      } else {
        sizes.forEach((size, sIdx) => {
          if (sIdx === 0) {
            matrix.push({
              color: colorName, size,
              ...imageFields
            })
          } else {
            matrix.push({ color: colorName, size })
          }
        })
      }
    })
    return matrix
  }

  const buildProductJsonForProduct = (pi) => {
    const cached = productCache.value[pi]
    const productForm = cached.form
    const productAnalyzed = cached.analyzedFields
    const productVariantColors = cached.variantColors
    const productMapping = getImageMapping(pi)
    const productCopy = getCopyData(pi)
    const productCategory = categories.value.find(c => c.key === productForm.categoryKey)

    if (!productCategory) throw new Error(`${PRODUCT_LABELS[pi]} 未选择商品类别`)

    const fields = {}
    fields.product_title = productCopy?.title || ''
    fields.parent_sku = productForm.sku
    fields.brand = productForm.brand || ''
    fields.product_category = productCategory.productCategory
    fields.sell_type = getSellType(pi)
    fields.description = productCopy?.introduction || ''
    fields.bullet_points = (productCopy?.features || []).slice(0, 5)
    fields.item_condition = 'New(新的)'

    productCategory.fields.forEach(f => {
      const aiVal = productAnalyzed[f.key]
      const hasValue = Array.isArray(aiVal)
        ? aiVal.length > 0
        : (aiVal !== undefined && aiVal !== '')
      if (hasValue) {
        fields[f.key] = aiVal
      } else if (f.defaultValue && f.defaultValue !== 'false') {
        fields[f.key] = f.defaultValue
      }
    })

    if (productForm.price) fields['List Price'] = productForm.price
    fields['List Price Currency'] = 'USD(美元)'

    const product = {
      schema_version: '1.0',
      product_category: productCategory.productCategory,
      sell_type: getSellType(pi),
      store_account: productForm.storeAccount,
      fields
    }

    if (getIsMultiVariant(pi)) {
      const selectedSizes = (productForm.sizeList || '').split(/[,，]/).map(s => s.trim()).filter(Boolean)
      const copywritingSizeType = workbench.copywriting?.inputs?.[pi]?.sizeType
      const selectedOneSize = selectedSizes.length === 1 && /^one\s*size\b/i.test(selectedSizes[0])
      const isOneSize = copywritingSizeType === 'one-size' || selectedOneSize
      const sizes = isOneSize ? [] : selectedSizes
      const colors = productMapping.variants.map(v => productVariantColors[v.colorValue] || v.colorValue)
      product.multi_variant = {
        variation_theme: sizes.length > 0 ? 'COLOR/SIZE' : 'COLOR',
        dimensions: [
          { name: 'Color', values: colors },
          ...(sizes.length > 0 ? [{ name: 'Size', values: sizes }] : [])
        ],
        price: productForm.price || '',
        quantity: productForm.quantity || '',
        matrix: buildMatrix(productMapping.variants, sizes, productVariantColors)
      }
    } else {
      product.images = productMapping.single
        .map((img, i) => img.url ? `images/main_${i + 1}.png` : '')
        .filter(Boolean)
    }

    return product
  }

  const buildProductJson = () => {
    syncCurrentToCache()
    return buildProductJsonForProduct(currentProductIndex.value)
  }

  const buildImageListForProduct = (pi) => {
    const list = []
    const mapping = getImageMapping(pi)
    if (getIsMultiVariant(pi)) {
      mapping.variants.forEach(v => {
        if (v.main) list.push({ url: v.main, path: `images/main_${v.vi + 1}.png` })
        if (v.swatch) list.push({ url: v.swatch, path: `images/swatch_${v.vi + 1}.png` })
        v.sub.forEach((url, ri) => {
          if (url) list.push({ url, path: `images/sub_${v.vi + 1}_${ri + 1}.png` })
        })
      })
    } else {
      mapping.single.forEach((img, i) => {
        list.push({ url: img.url, path: `images/main_${i + 1}.png` })
      })
    }
    return list
  }

  // 把一个产品的打包任务加入后台队列（立即返回，不阻塞 UI）
  const enqueuePackJob = (pi) => {
    const cached = productCache.value[pi]
    if (!cached?.form?.sku) {
      throw new Error(`${PRODUCT_LABELS[pi]} 缺 SKU`)
    }
    const job = reactive({
      id: ++_jobSeq,
      pi,
      label: PRODUCT_LABELS[pi],
      sku: cached.form.sku,
      status: 'queued',        // queued | packing | done | error
      error: '',
      resultUrl: '',
      filename: '',
      createdAt: Date.now()
    })
    packJobs.value.unshift(job)
    // 串行入队：保证后端一次只处理一个打包任务，避免内存峰值
    _queueTail = _queueTail.then(() => _executeJob(job, pi))
    return job
  }

  const downloadPack = () => {
    syncCurrentToCache()
    buildError.value = ''
    try {
      enqueuePackJob(currentProductIndex.value)
    } catch (e) {
      buildError.value = e.message || '打包失败'
      throw e
    }
  }

  const downloadAllPacks = () => {
    syncCurrentToCache()
    buildError.value = ''
    try {
      for (let pi = 0; pi < PRODUCT_COUNT; pi++) {
        if (productCache.value[pi]?.form?.sku) {
          enqueuePackJob(pi)
        }
      }
    } catch (e) {
      buildError.value = e.message || '打包失败'
      throw e
    }
  }

  return {
    categories, categoriesLoading,
    currentProductIndex, productCount: PRODUCT_COUNT, productLabels: PRODUCT_LABELS,
    switchProduct,
    form, analyzedFields, analyzing, analyzeError,
    variantColors, colorOptions: COLOR_OPTIONS,
    building, buildError,
    sellType, isMultiVariant, colorCount, sizeValues,
    currentCategory, currentTemplateFields, aiDerivableFields,
    productImages, imageMapping, copyData,
    uploadMappingImage,
    loadCategories, runAnalyze,
    downloadPack, downloadAllPacks, buildProductJson,
    packJobs, hasActiveJob, redownloadPack, clearFinishedJobs,
    sizeDiag
  }
})
